import {html, LitElement, TemplateResult} from "lit";
import {customElement, state} from "lit/decorators.js";
import {leaderboardRequest, loginUser, registerUser, startGame} from "../services/routeService";
import styles from "../styles/startCanvasStyles";
import {difficultyEnum, difficultyTimeMap} from "../utils/handeEnums";
import {getDifficultyFromSession} from "../utils/gameUtils";

@customElement("start-canvas")
export class StartCanvas extends LitElement {
    private leaderboardData: any[] = [];
    private isLoading: boolean = true;
    public static styles = [styles];

    private countdownActive: boolean = false;

    @state() private showLoginForm: boolean = false;
    @state() private showSignupForm: boolean = false;
    @state() private textInput: string = "";

    @state() private password: string = "";
    @state() private passwordValid: boolean = true;
    @state() private ValidationMessage: string = "";
    @state() private successMessage: string = "";
    @state() private isLengthValid: boolean = false;
    @state() private isLowercaseValid: boolean = false;
    @state() private isUppercaseValid: boolean = false;
    @state() private isNumberValid: boolean = false;
    @state() private isSpecialCharValid: boolean = false;
    @state() private allowStartGame: boolean = true;
    @state() private authSuccess: boolean = false;
    @state() private startGameMessage: string = "";
    @state() private guestPlayer: boolean = false;

    @state() private difficulty: difficultyEnum = difficultyEnum.unset;

    public async connectedCallback(): Promise<void> {
        super.connectedCallback();
        await this.fetchLeaderboardData();
        this.clearLocalSession();
    }

    private clearLocalSession(): void {
        localStorage.removeItem("countdownEndTime");
        localStorage.removeItem("playerSessionId");
        localStorage.removeItem("game-started");
    }

    private handleClickAction(): void {
        const timeForCurrentDifficulty: number = difficultyTimeMap[this.difficulty] * 60;

        if (!this.authSuccess || this.difficulty === difficultyEnum.unset) {
            this.startGameMessage = "Please make sure you are logged in and set the difficulty!";
            return;
        }

        if (!this.countdownActive) {
            this.startCountdown(timeForCurrentDifficulty * 1000, (endTime: number) => {
                console.log(`Countdown ending at: ${new Date(endTime).toLocaleTimeString()}`);
                this.countdownActive = true;
            }, this.countdownActive);

            localStorage.setItem("game-started", String(true));
            void startGame();
            location.reload();
        }

        this.requestUpdate();
    }

    public startCountdown = (duration: number, updateFunc: (endTime: number) => void, countdownActive: boolean): void => {
        if (!countdownActive) {
            const endTime: number = Date.now() + duration;
            localStorage.setItem("countdownEndTime", endTime.toString());
            updateFunc(endTime);
        }
    };

    private async fetchLeaderboardData(): Promise<void> {
        this.isLoading = true;
        try {
            const scores: any = await leaderboardRequest();
            if (Array.isArray(scores)) {
                this.leaderboardData = scores;
            } else {
                console.error("Failed to fetch leaderboard data: Data is not an array");
                this.leaderboardData = [];
            }
        } catch (error) {
            console.error("Error fetching leaderboard data:", error);
            this.leaderboardData = [];
        } finally {
            this.isLoading = false;
            this.requestUpdate();
        }
    }

    private setDifficultyInSession(difficulty: difficultyEnum): void {
        localStorage.setItem("difficulty", difficulty);
    }

    private setDifficulty(difficulty: string): void {
        switch (difficulty) {
            case "normal":
                this.setDifficultyInSession(difficultyEnum.normal);
                this.difficulty = difficultyEnum.normal;
                break;
            case "hard":
                this.setDifficultyInSession(difficultyEnum.hard);
                this.difficulty = difficultyEnum.hard;
                break;
            case "hell":
                this.setDifficultyInSession(difficultyEnum.hell);
                this.difficulty = difficultyEnum.hell;
                break;
            case "unset":
                localStorage.removeItem("difficulty");
                this.difficulty = difficultyEnum.unset;
                break;
        }
    }

    private handleUser(type: string): void {
        switch (type) {
            case "guest":
                this.showSignupForm = false;
                this.showLoginForm = false;
                if (this.showSignupForm) this.resetPasswordValidation();
                this.authSuccess = true;
                this.guestPlayer = true;
                localStorage.setItem("playerType", "guest");
                break;

            case "login":
                this.showLoginForm = !this.showLoginForm;
                this.showSignupForm = false;
                if (this.showLoginForm) this.resetPasswordValidation();
                break;

            case "signup":
                this.showSignupForm = !this.showSignupForm;
                this.showLoginForm = false;
                if (this.showSignupForm) this.resetPasswordValidation();
                break;

            case "logout":
                this.authSuccess = false;
                localStorage.removeItem("jwtToken");
                break;

            case "return":
                this.authSuccess = false;
                localStorage.removeItem("playerType");
                break;

            default:
                break;
        }

    }

    private resetPasswordValidation(): void {
        this.password = "";
        this.isLengthValid = false;
        this.isLowercaseValid = false;
        this.isUppercaseValid = false;
        this.isNumberValid = false;
        this.isSpecialCharValid = false;
        this.passwordValid = false;
        this.requestUpdate();
    }


    private async handleLogin(): Promise<void> {
        const password: string = this.password;
        const username: string = this.textInput;

        const logInUser: Response | undefined = await loginUser(username, password);
        if (!logInUser?.ok) {
            throw new Error("Network response was not ok");
        }
        const json: any = await logInUser.json();
        const JWTToken: string = json.token;

        localStorage.setItem("jwtToken", JWTToken);

        this.successMessage = "Successfully logged in!";

        this.authSuccess = true;

    }

    private async handleSignup(): Promise<void> {
        const password: string = this.password;
        const username: string = this.textInput;
        if (this.passwordValid) {
            const registered: Response | undefined = await registerUser(username, password);

            if (registered?.status === 409) {
                this.ValidationMessage = "Username is already taken.";
                this.successMessage = "";
                this.requestUpdate();
            }

            if (registered?.status === 201) {
                const logInUser: Response | undefined = await loginUser(username, password);
                if (!logInUser?.ok) {
                    throw new Error("Network response was not ok");
                }
                const json: any = await logInUser.json();
                const JWTToken: string = json.token;

                localStorage.setItem("jwtToken", JWTToken);

                this.successMessage = "Successfully created account!";

                this.authSuccess = true;
                this.showSignupForm = false;
            }
        }
        return undefined;
    }

    private userInput(evt: Event): void {
        const target: HTMLInputElement = evt.target as HTMLInputElement;
        this.ValidationMessage = "";
        if (target.type === "password") {
            this.password = target.value;
            this.validatePassword();
        } else {
            this.textInput = target.value;
        }
    }

    private validatePassword(): void {
        this.isLengthValid = /.{8,}/.test(this.password);
        this.isLowercaseValid = /[a-z]/.test(this.password);
        this.isUppercaseValid = /[A-Z]/.test(this.password);
        this.isNumberValid = /[0-9]/.test(this.password);
        this.isSpecialCharValid = /[!@#$%^&*(),.?":{}|<>]/.test(this.password);

        this.passwordValid = this.isLengthValid && this.isLowercaseValid && this.isUppercaseValid && this.isNumberValid && this.isSpecialCharValid;
    }


    private renderForm(type: string): TemplateResult {
        return html`
            <div class="form-container">
                <input type="text" placeholder="Username" @input="${this.userInput}" class="retro-input"/>
                <input type="password" placeholder="Password" @input="${this.userInput}" class="retro-input"/>
                ${type === "signup" ? html`${this.renderPasswordValidation()}` : ""}
                ${type === "login" ? html`
                    <a class="form-button" tabindex="0"
                       @click=${this.handleLogin}
                       @keydown=${async (e: KeyboardEvent): Promise<void> => {
                           if (e.key === "Enter") await this.handleLogin();
                       }}
                    >Login</a>
                ` : ""}
                ${type === "signup" ? html`
                    <a class="form-button" tabindex="0"
                       @click=${this.handleSignup}
                       @keydown=${async (e: KeyboardEvent): Promise<void> => {
                           if (e.key === "Enter") await this.handleSignup();
                       }}
                    >Signup</a>
                ` : ""}
            </div>
        `;
    }

    private renderPasswordValidation(): TemplateResult {
        return html`
            <div class="password-validation">
                <p><span class="${this.isLengthValid ? "valid" : "invalid"}">${this.isLengthValid ? "✔" : "✖"} At least 8 characters</span>
                </p>
                <p><span
                    class="${this.isLowercaseValid ? "valid" : "invalid"}">${this.isLowercaseValid ? "✔" : "✖"} Contains one lowercase letter</span>
                </p>
                <p><span
                    class="${this.isUppercaseValid ? "valid" : "invalid"}">${this.isUppercaseValid ? "✔" : "✖"} Contains one uppercase letter</span>
                </p>
                <p><span class="${this.isNumberValid ? "valid" : "invalid"}">${this.isNumberValid ? "✔" : "✖"} Contains one number</span>
                </p>
                <p><span
                    class="${this.isSpecialCharValid ? "valid" : "invalid"}">${this.isSpecialCharValid ? "✔" : "✖"} Contains one special character (@, #, $, %, etc.)</span>
                </p>
            </div>
        `;
    }


    private renderLoggingInUI(): TemplateResult {
        return html`
            <div class="action-container">
                <a class="action-button" tabindex="0"
                   @click=${(): void => this.handleUser("guest")}
                   @keydown=${(e: KeyboardEvent): void => {
                       if (e.key === "Enter") this.handleUser("guest");
                   }}
                >Guest</a>

                ${!this.showLoginForm ? html`
                    <a class="action-button" tabindex="0"
                       @click=${(): void => this.handleUser("login")}
                       @keydown=${(e: KeyboardEvent): void => {
                           if (e.key === "Enter") this.handleUser("login");
                       }}
                    >Login</a>
                ` : ""}

                ${!this.showSignupForm ? html`
                    <a class="action-button" tabindex="0"
                       @click=${(): void => this.handleUser("signup")}
                       @keydown=${(e: KeyboardEvent): void => {
                           if (e.key === "Enter") this.handleUser("signup");
                       }}
                    >Signup</a>
                ` : ""}

            </div>

            ${this.showLoginForm ? this.renderForm("login") : ""}
            ${this.showSignupForm ? this.renderForm("signup") : ""}

            <div class="box-items">
                ${this.ValidationMessage.length > 0 ? html`
                    <div class="validation-message">${this.ValidationMessage}</div>
                ` : (this.successMessage ? html`
                    <div class="validation-message">${this.successMessage}</div>
                ` : "")}
            </div>
        `;
    }


    private renderStartUI(): TemplateResult {
        const loggedIn: string | null = localStorage.getItem("jwtToken");
        const guestPlayer: string | null = localStorage.getItem("playerType");
        if (loggedIn) {
            this.authSuccess = true;
        }

        if (guestPlayer) {
            this.guestPlayer = true;
            this.authSuccess = true;
        }

        const getDifficulty: difficultyEnum | null = getDifficultyFromSession();

        if (getDifficulty) {
            this.setDifficulty(getDifficulty);
        }

        return html`
            <div class="start-box">
                <h2 class="title">Welcome to the Game</h2>
                <div class="box-items">
                    ${this.authSuccess && !this.guestPlayer ? html`
                        <a class="action-button" tabindex="0"
                           @click=${(): void => this.handleUser("logout")}
                           @keydown=${(e: KeyboardEvent): void => {
                               if (e.key === "Enter") this.handleUser("logout");
                           }}
                        >Logout</a>
                    ` : (this.authSuccess && this.guestPlayer ? html`
                        <a class="action-button" tabindex="0"
                           @click=${(): void => this.handleUser("return")}
                           @keydown=${(e: KeyboardEvent): void => {
                               if (e.key === "Enter") this.handleUser("return");
                           }}
                        >Sign In / Sign Up</a>
                    ` : "")}
                </div>
                ${!this.authSuccess ? this.renderLoggingInUI() : ""}


                ${this.authSuccess ? html`
                    <h3 class="title" style="margin-top: 1rem">
                        ${this.difficulty === difficultyEnum.unset ? "Set difficulty" : `Difficulty: ${this.difficulty}`}
                    </h3>

                    ${this.difficulty === difficultyEnum.unset ? this.renderDifficulties() : html`
                        <div class="box-items">
                            <a class="action-button" tabindex="0"
                               @click=${(): void => this.setDifficulty("unset")}
                               @keydown=${(e: KeyboardEvent): void => {
                                   if (e.key === "Enter") this.setDifficulty("unset");
                               }}
                            >Change difficulty</a>
                        </div>
                    `}
                ` : ""}
            </div>
        `;
    }

    private renderDifficulties(): TemplateResult {
        return html`
            <div class="box-items">
                <a class="action-button" tabindex="0"
                   @click=${(): void => this.setDifficulty("normal")}
                   @keydown=${(e: KeyboardEvent): void => {
                       if (e.key === "Enter") this.setDifficulty("normal");
                   }}
                >Normal</a>
                <a class="action-button" tabindex="0"
                   @click=${(): void => this.setDifficulty("hard")}
                   @keydown=${(e: KeyboardEvent): void => {
                       if (e.key === "Enter") this.setDifficulty("hard");
                   }}
                >Hard</a>
                <a class="action-button" tabindex="0"
                   @click=${(): void => this.setDifficulty("hell")}
                   @keydown=${(e: KeyboardEvent): void => {
                       if (e.key === "Enter") this.setDifficulty("hell");
                   }}
                >Hell</a>
            </div>
        `;
    }


    private startGamebutton(): TemplateResult {
        return html`
            <div class="start-box button-box">
                <a class="start-button" tabindex="0"
                   @click=${(): void => {
                       this.handleClickAction();
                   }}
                   @keydown=${(e: KeyboardEvent): void => {
                       if (e.key === "Enter") this.handleClickAction();
                   }}
                >Start Game</a>
                ${this.startGameMessage.length > 0 ? html`
                    <div class="validation-message">${this.startGameMessage}</div>
                ` : ""}
            </div>
        `;
    }

    private renderScoreboard(): TemplateResult {
        if (this.isLoading) {
            return this.renderSkeleton();
        } else {
            return html`
                <div class="scoreboard">
                    <h2 class="title">Leaderboard</h2>
                    <div class="score-list">
                        ${this.leaderboardData.map((entry, index) =>
                            this.renderScoreItem(index + 1, entry.username ?? "Guest", entry.completionTime)
                        )}
                    </div>
                </div>
            `;
        }
    }


    private renderSkeleton(): TemplateResult {
        return html`
            <div class="scoreboard">
                <h2 class="title">Leaderboard</h2>
                ${Array(5).fill(0).map(() =>
                    html`
                        <div class="score-item skeleton">
                            <span class="skeleton-text"></span>
                            <span class="skeleton-text"></span>
                        </div>`
                )}
            </div>
        `;
    }

    private renderScoreItem(rank: number, name: string, completionTime: number): TemplateResult {
        const totalSeconds: number = Math.floor(completionTime / 1000); // Convert milliseconds to seconds
        const minutes: number = Math.floor(totalSeconds / 60); // Extract minutes
        const seconds: number = totalSeconds % 60; // Extract seconds
        return html`
            <div class="score-item"><span>${rank}: ${name}</span><span>${minutes} min ${seconds} sec</span>
            </div>
        `;
    }

    protected render(): TemplateResult {
        return html`
            <div class="outer-container">
                <div class="container">
                    ${this.renderStartUI()}
                    ${this.renderScoreboard()}
                </div>

                <div class="container">
                    ${this.allowStartGame ? this.startGamebutton() : ""}
                </div>
            </div>
        `;
    }
}
